import { useCallback } from 'react';

import { useGetRelatedPeopleQuery } from '@shared/services/caregivers';
import type { RelatedPersonTypeType } from '@shared/services/caregivers/types/getRelatedPeople.types';
import { RelatedPersonType } from '@shared/services/caregivers/types/getRelatedPeople.types';

import useIsAuthenticated from '../useIsAuthenticated';

interface Props {
  relatedPersonType?: RelatedPersonTypeType;
  skip?: boolean;
}

const useRelatedPeople = (props?: Props) => {
  const { isAuthenticated } = useIsAuthenticated();

  const {
    data: relatedPeopleData,
    error: relatedPeopleError,
    isFetching: isFetchingRelatedPeople,
    isLoading: isLoadingRelatedPeople,
    refetch: refetchRelatedPeople,
  } = useGetRelatedPeopleQuery(
    {
      RelatedPersonType: props?.relatedPersonType || RelatedPersonType.ALL,
      Sort: 0,
    },
    {
      skip: !isAuthenticated || props?.skip,
    }
  );

  const getMemberFromPrn = useCallback(
    (memberIdentifier: string, type: RelatedPersonTypeType) => {
      if (!relatedPeopleData) return null;

      if (type === RelatedPersonType.CAREGIVERS) {
        return relatedPeopleData.Caregivers?.find((member) => member.Prn === memberIdentifier);
      }

      if (type === RelatedPersonType.CHILDREN) {
        return relatedPeopleData.Children?.find((member) => member.Prn === memberIdentifier);
      }

      return relatedPeopleData.FamilyAndFriend?.find((member) => member.Prn === memberIdentifier);
    },
    [relatedPeopleData]
  );

  return {
    data: relatedPeopleData,
    error: relatedPeopleError,
    getMemberFromPrn,
    isFetching: isFetchingRelatedPeople,
    isLoading: isLoadingRelatedPeople,
    refetch: refetchRelatedPeople,
  };
};

export default useRelatedPeople;
